import { createFileRoute } from '@tanstack/react-router'
import { Plus, Calendar, Clock, Filter, ChevronDown, List, CalendarDays } from 'lucide-react'
import { useState } from 'react'

import { ExportButton } from '@/components/common/export-button'
import { TimelogCalendarView } from '@/components/pm/timelog/timelog-calendar-view'
import { TimelogForm } from '@/components/pm/timelog/timelog-form'
import { TimelogStats } from '@/components/pm/timelog/timelog-stats'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useConfirmDialog } from '@/hooks'
import {
  useTimelogs,
  useTimelogStats,
  useDeleteTimelog,
  type TimelogListParams,
} from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 工时列表页面
 */
export const Route = createFileRoute('/_authenticated/pm/timelogs/')({
  component: TimelogListPage,
})

function TimelogListPage() {
  // 默认日期范围：不设置（显示所有记录）
  // 日期输入框为空时浏览器会显示占位符
  const [filters, setFilters] = useState<TimelogListParams>({
    page: 1,
    pageSize: 20,
    sortBy: 'logDate',
    sortOrder: 'desc',
  })
  const [showFilters, setShowFilters] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showStats, setShowStats] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')

  const { data: timelogs, isLoading, error } = useTimelogs(filters)
  const { data: stats } = useTimelogStats({
    startDate: filters.startDate,
    endDate: filters.endDate,
    projectId: filters.projectId,
    employeeId: filters.employeeId,
  })
  const deleteMutation = useDeleteTimelog()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setFilters((prev) => ({ ...prev, startDate, endDate, page: 1 }))
  }

  const handleDelete = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'delete',
      '确定要删除这条工时记录吗？'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleEdit = (id: string) => {
    setEditingId(id)
    setShowForm(true)
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingId(null)
  }

  // 格式化分钟为小时
  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours === 0) {
      return `${mins}分钟`
    }
    if (mins === 0) {
      return `${hours}小时`
    }
    return `${hours}小时${mins}分钟`
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">工时记录</h1>
          <p className="text-muted-foreground">记录和管理工作时间</p>
        </div>
        <div className="flex items-center gap-2">
          {/* 视图切换 */}
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'calendar')}>
            <TabsList>
              <TabsTrigger value="list" className="gap-1">
                <List className="h-4 w-4" />
                列表
              </TabsTrigger>
              <TabsTrigger value="calendar" className="gap-1">
                <CalendarDays className="h-4 w-4" />
                日历
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <button
            onClick={() => setShowStats(!showStats)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showStats ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <Clock className="h-4 w-4" />
            统计
          </button>
          <ExportButton
            type="timelogs"
            filters={{
              startDate: filters.startDate,
              endDate: filters.endDate,
              projectId: filters.projectId,
              employeeId: filters.employeeId,
            }}
          />
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            记录工时
          </button>
        </div>
      </div>

      {/* 统计面板 */}
      {showStats && stats && (
        <TimelogStats stats={stats} />
      )}

      {/* 日历视图 */}
      {viewMode === 'calendar' ? (
        <TimelogCalendarView dailyGoal={8} />
      ) : (
        <>
          {/* 筛选区域 */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* 日期范围 */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <input
                  type="date"
                  value={filters.startDate ?? ''}
                  onChange={(e) => handleDateRangeChange(e.target.value, filters.endDate ?? '')}
                  placeholder="开始日期"
                  className="rounded-md border bg-background px-3 py-2 text-sm"
                />
                <span className="text-muted-foreground">至</span>
                <input
                  type="date"
                  value={filters.endDate ?? ''}
                  onChange={(e) => handleDateRangeChange(filters.startDate ?? '', e.target.value)}
                  placeholder="结束日期"
                  className="rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>

              {/* 筛选按钮 */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                  showFilters ? 'bg-muted' : 'hover:bg-muted'
                )}
              >
                <Filter className="h-4 w-4" />
                更多筛选
                <ChevronDown className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')} />
              </button>
            </div>

            {/* 更多筛选面板 */}
            {showFilters && (
              <div className="rounded-lg border bg-card p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* 项目筛选 */}
                  <div>
                    <label className="mb-2 block text-sm font-medium">项目</label>
                    <Select
                      value={filters.projectId ?? 'all'}
                      onValueChange={(value) => setFilters((prev) => ({ ...prev, projectId: value === 'all' ? undefined : value, page: 1 }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="全部项目" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部项目</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* 员工筛选 */}
                  <div>
                    <label className="mb-2 block text-sm font-medium">员工</label>
                    <Select
                      value={filters.employeeId ?? 'all'}
                      onValueChange={(value) => setFilters((prev) => ({ ...prev, employeeId: value === 'all' ? undefined : value, page: 1 }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="全部员工" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部员工</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 汇总信息 */}
          {stats && (
            <div className="flex items-center gap-6 rounded-lg border bg-card p-4">
              <div>
                <div className="text-sm text-muted-foreground">总工时</div>
                <div className="text-2xl font-bold">{stats.totalHours.toFixed(1)} 小时</div>
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <div className="text-sm text-muted-foreground">记录数</div>
                <div className="text-2xl font-bold">{timelogs?.length || 0}</div>
              </div>
            </div>
          )}

          {/* 工时列表 */}
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="text-muted-foreground">加载中...</div>
            </div>
          ) : !timelogs || timelogs.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
              <Clock className="h-12 w-12 text-muted-foreground/50" />
              <div className="text-center">
                <p className="text-lg font-medium">暂无工时记录</p>
                <p className="text-sm text-muted-foreground">点击上方按钮记录第一条工时</p>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">日期</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">任务</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">工时</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">描述</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {timelogs.map((timelog) => (
                    <tr key={timelog.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">
                        {new Date(timelog.logDate).toLocaleDateString('zh-CN')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {timelog.issue ? (
                          <span title={timelog.issue.title}>
                            <span className="font-mono text-muted-foreground">{timelog.issue.code}</span>
                            <span className="ml-2">{timelog.issue.title}</span>
                          </span>
                        ) : (
                          <span className="font-mono text-muted-foreground">{timelog.issueId.slice(0, 8)}...</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-sm font-medium">
                          <Clock className="h-3.5 w-3.5" />
                          {formatMinutes(timelog.minutes)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {timelog.description || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(timelog.id)}
                            className="text-sm text-primary hover:underline"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => void handleDelete(timelog.id)}
                            className="text-sm text-destructive hover:underline"
                            disabled={deleteMutation.isPending}
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* 工时表单弹窗 */}
      {showForm && (
        <TimelogForm
          timelogId={editingId}
          onClose={handleFormClose}
        />
      )}
      <ConfirmDialog />
    </div>
  )
}
